import buildSiteplanStatsHtml from '../services/siteplanStatsService';
import LotStatus from '../enums/lotStatus';

/**
 * Renders a single quick-move-in card.
 * @param {Object} card - Quick-move-in card data.
 * @returns {string} HTML string for one card.
 */
export function renderQuickMoveInCard(card = {}) {
  const {
    imageAlt = card.name?.trim()
      ? `${card.name.trim()} quick move-in hero image`
      : 'quick move-in hero image',
    lotLabel = card.lotDisplayLabel ?? null,
    statusLabel = null,
  } = card;

  // NHS20-9017: a Model home unit shows "Model home" in place of its price, everywhere else.
  const isModel = card.lotStatus === LotStatus.Model.toString();

  const resolvedLotLabel = lotLabel ? `Lot ${lotLabel}` : null;

  const lotBadgeHtml = resolvedLotLabel
    ? `<div class="nhs-c-siteplan-card__badge">${resolvedLotLabel}</div>`
    : '';

  const statusBadgeHtml = statusLabel
    ? `<div class="nhs-c-siteplan-card__badge nhs-c-siteplan-card__badge--available">${statusLabel}</div>`
    : '';
  const badgesHtml =
    lotBadgeHtml || statusBadgeHtml
      ? `
      <div class="nhs-c-siteplan-card__badges" data-qa="native_ism_quick_move_in_badge">
        ${lotBadgeHtml ? `<div class="nhs-c-siteplan-card__badge nhs-mq-siteplan-card__badge-medium">${resolvedLotLabel}</div>` : ''}
        ${statusBadgeHtml ? `<div class="nhs-c-siteplan-card__badge nhs-c-siteplan-card__badge--available nhs-mq-siteplan-card__badge-medium">${statusLabel}</div>` : ''}
      </div>
    `
      : '';

  const statsHtml = buildSiteplanStatsHtml([
    { value: card.bedrooms, label: 'Bedrooms', suffix: 'Bd', isNumeric: true },
    { value: card.formattedBathrooms, label: 'Baths', suffix: 'Ba', isNumeric: true },
    { value: card.garage, label: 'Garage', suffix: 'Gr', isNumeric: true },
    { value: card.formattedSquareFeet, label: 'Square Feet', suffix: 'SqFt' },
  ]);

  return `
    <article class="nhs-c-siteplan-card" data-housing-unit-id="${card.specificationId}" data-qa="native_ism_quick_move_in_card">
      <div class="nhs-c-siteplan-card__image-wrapper nhs-mq-siteplan-card__image-wrapper-medium">
        <img class="nhs-c-siteplan-card__image" data-siteplan-card-image
              loading="lazy"
              src="${card.image}"
              alt="${imageAlt}"
              data-qa="native_ism_quick_move_in_card_image" />
      </div>
      ${badgesHtml}
      <div class="nhs-c-siteplan-card__content">
        <h4 class="nhs-c-siteplan-card__title nhs-mq-siteplan-card__title-medium" data-qa="native_ism_quick_move_in_title">${card.name}</h4>
        <p class="nhs-c-siteplan-card__price nhs-mq-siteplan-card__price-medium" data-qa="native_ism_quick_move_in_price">${isModel ? 'Model home' : card.formattedPrice}</p>
        ${statsHtml ? `<div class="nhs-c-siteplan-card__stats" data-qa="native_ism_quick_move_in_stats">${statsHtml}</div>` : ''}
      </div>
    </article>
  `;
}

/**
 * Renders the quick-move-in cards grid.
 * @param {Object[]} cards - Quick-move-in card list.
 * @returns {string} HTML string for panel content.
 */
export function renderQuickMoveInGrid(cards = []) {
  const cardsHtml = cards.map((card) => renderQuickMoveInCard(card)).join('');
  const gridClasses = [
    'nhs-c-native-interactive-site-map__card-grid',
    'nhs-mq-native-interactive-site-map__card-grid-medium',
    'nhs-mq-native-interactive-site-map__card-grid-large',
  ];

  if (cards.length > 1) {
    gridClasses.push('nhs-c-native-interactive-site-map__card-grid--scroll');
  } 

  return `
    <div class="${gridClasses.join(' ')}" data-qa="native_ism_quick_move_in_grid">
      ${cardsHtml}
    </div>
  `;
}
